import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Input,
  Tabs,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { FileBracketsAsset } from '@kula-ai/assets';
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Copy03,
  SearchMd,
} from '@kula-ai/icons-core';
import { ESIGN_ACTIVITY_COPY } from '@/constants/esignature';
import { colors } from '@/theme/foundations/colors';
import type { EsignWizardDocument } from '@/types/esignature';
import {
  formatToken,
  invalidTokensOf,
  isDocumentInvalid,
  tokenDisplayName,
  validTokensOf,
} from './esignDocuments';
import { useEsignSupportedTokens } from './useEsignSupportedTokens';

const COPY_FEEDBACK_MS = 1000;

interface TokenRowProps {
  labels?: Map<string, string>;
  token: string;
}

/** Name over a copyable chip — the shape the offer-letter token panel uses. */
const TokenRow = ({ token, labels }: TokenRowProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(formatToken(token));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), COPY_FEEDBACK_MS);
  }, [token]);

  // Figma (7:4983) pitches these rows 74px apart: a 14/24 label, a 6px gap, a
  // 32px chip row, and the list's own 12px between rows.
  return (
    <Flex flexDir="column" gap="6px">
      <Text className="!font-semibold !leading-6 !text-secondary-900">
        {tokenDisplayName(token, labels)}
      </Text>
      <Flex align="center" gap="6px" overflow="hidden">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Box minW={0} overflow="hidden">
                {/* Figma's chip: 24px tall, 2px/8px padding, 6px radius on
                    secondary-50. kula's Tag is taller and rounder. */}
                <Tag
                  label={formatToken(token)}
                  variant="neutral"
                  size="md"
                  className="!h-6 !rounded-md !border-0 !bg-secondary-50 !px-2 !py-0.5"
                />
              </Box>
            </TooltipTrigger>
            <TooltipContent side="top" collisionPadding={12}>
              {formatToken(token)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Figma draws the copy affordance as a bordered square button beside
            the chip, not a bare glyph. */}
        <Box
          as="button"
          type="button"
          aria-label={`Copy ${tokenDisplayName(token, labels)} token`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="32px"
          h="32px"
          flexShrink={0}
          borderRadius="6px"
          border="1px solid"
          borderColor="border-color"
          bg="white"
          cursor="pointer"
          color={isCopied ? 'pastel-2-dark' : 'secondary-700'}
          _hover={{ bg: 'secondary-25' }}
          onClick={handleCopy}
        >
          {isCopied ? <Check size={16} /> : <Copy03 size={16} />}
        </Box>
      </Flex>
    </Flex>
  );
};

/**
 * The rail's content sits in a fixed 301px column centred in the 340px panel
 * (Figma 7:4983), not a full-bleed block with padding.
 */
const RailBlock = ({ children }: { children: React.ReactNode }) => (
  <Flex flexDir="column" align="center" pt="20px" pb="16px">
    <Flex flexDir="column" w="301px" gap="16px">
      {children}
    </Flex>
  </Flex>
);

/**
 * A token the server could not resolve. No copy button — copying a name that
 * does not exist only spreads it — but it truncates and carries the full text
 * in a tooltip, since a long one used to push the 340px rail sideways.
 */
const InvalidTokenChip = ({ token }: { token: string }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Flex
          align="center"
          maxW="100%"
          px="8px"
          py="2px"
          bg="pastel-1-light"
          borderRadius="6px"
          data-test-id="esign-invalid-token"
        >
          <Text variant="p" color="error" truncate>
            {formatToken(token)}
          </Text>
        </Flex>
      </TooltipTrigger>
      <TooltipContent side="top" collisionPadding={12}>
        {formatToken(token)}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const RailCaption = ({ children }: { children: React.ReactNode }) => (
  <Text variant="overhead" className="!text-secondary-600">
    {children}
  </Text>
);

/**
 * The catalogue of tokens a document may contain.
 *
 * Grouped and searchable because the set is ~72, not the six this rail was
 * built for: a flat list that long in a 301px column is a scroll, not a
 * reference. Groups come from the server so the frontend is not guessing
 * categories from token prefixes.
 *
 * Every group starts open — the same reasoning as the uploaded-file rows:
 * finding a token is the whole job here, and a closed accordion hides it.
 */
const AvailableTokens = () => {
  const { groups, labels, isLoading, isUnavailable } =
    useEsignSupportedTokens();
  const [query, setQuery] = useState('');

  const term = query.trim().toLowerCase();
  const matches = term
    ? groups
        .map((group) => ({
          ...group,
          tokens: group.tokens.filter(
            ({ token, label }) =>
              token.toLowerCase().includes(term) ||
              label.toLowerCase().includes(term)
          ),
        }))
        .filter((group) => group.tokens.length > 0)
    : groups;

  return (
    <RailBlock>
      <RailCaption>{ESIGN_ACTIVITY_COPY.AVAILABLE_TOKENS_CAPTION}</RailCaption>

      {!isLoading && !isUnavailable && (
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={ESIGN_ACTIVITY_COPY.TOKEN_SEARCH_PLACEHOLDER}
          startIcon={<SearchMd size={16} />}
          aria-label={ESIGN_ACTIVITY_COPY.TOKEN_SEARCH_PLACEHOLDER}
          data-test-id="esign-token-search"
        />
      )}

      {isLoading && <Text variant="small">{ESIGN_ACTIVITY_COPY.LOADING}</Text>}

      {/* A failed catalogue is said plainly rather than shown as an empty rail.
          Nothing is blocked by it: the server still validates what is uploaded
          and still enforces the required anchor on save. */}
      {!isLoading && isUnavailable && (
        <Text variant="small" color="muted">
          {ESIGN_ACTIVITY_COPY.TOKENS_UNAVAILABLE}
        </Text>
      )}

      {matches.length === 0 && term && !isLoading && !isUnavailable && (
        <Text variant="small" color="muted">
          {ESIGN_ACTIVITY_COPY.TOKEN_SEARCH_EMPTY}
        </Text>
      )}

      <AccordionRoot
        type="multiple"
        value={matches.map((group) => group.name)}
        // Deliberately uncontrolled-in-effect: the open set is recomputed from
        // whatever currently matches, so searching never leaves a hit hidden
        // inside a group the recruiter happened to collapse earlier.
        onValueChange={() => {}}
      >
        {matches.map((group) => (
          <AccordionItem key={group.name} value={group.name}>
            <AccordionTrigger className="w-full min-w-0 [&>span]:min-w-0">
              <Flex align="center" gap="6px" flex="1" minW={0}>
                <Text variant="small" truncate>
                  {group.name}
                </Text>
                <Tag variant="white" size="sm">
                  {String(group.tokens.length)}
                </Tag>
              </Flex>
            </AccordionTrigger>
            <AccordionContent>
              <Flex flexDir="column" gap="12px" pt="8px">
                {group.tokens.map(({ token }) => (
                  <TokenRow key={token} token={token} labels={labels} />
                ))}
              </Flex>
            </AccordionContent>
          </AccordionItem>
        ))}
      </AccordionRoot>
    </RailBlock>
  );
};

interface IncludedTokensProps {
  documents: EsignWizardDocument[];
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  listRef: React.RefObject<HTMLDivElement>;
}

const IncludedTokens = ({
  documents,
  openKeys,
  onOpenChange,
  listRef,
}: IncludedTokensProps) => {
  // The same catalogue the Available tab reads, so one token is named
  // identically in both places rather than once from the server and once from
  // a guess made off its own prefix.
  const { labels } = useEsignSupportedTokens();

  if (documents.length === 0) {
    return (
      <Flex
        flexDir="column"
        align="center"
        justify="center"
        gap="16px"
        p="48px 24px"
        textAlign="center"
      >
        <FileBracketsAsset />
        <Text color="subtle">{ESIGN_ACTIVITY_COPY.INCLUDED_TOKENS_EMPTY}</Text>
      </Flex>
    );
  }

  return (
    <Box pt="20px" pb="16px" ref={listRef}>
      <Box px="16px" mb="16px">
        <RailCaption>{ESIGN_ACTIVITY_COPY.INCLUDED_TOKENS_CAPTION}</RailCaption>
      </Box>
      <AccordionRoot
        type="multiple"
        value={openKeys}
        onValueChange={onOpenChange}
      >
        {documents.map((document) => {
          const invalidTokens = invalidTokensOf(document.validation);
          const validTokens = validTokensOf(document.validation);
          const isInvalid = isDocumentInvalid(document);

          return (
            <AccordionItem key={document.key} value={document.key}>
              {/* The trigger sizes to its content, and kula wraps the children
                  in a `flex-1` span whose default `min-width: auto` refuses to
                  shrink — so a long filename pushed past the 340px rail rather
                  than clipping. Both need pinning to 0. */}
              <AccordionTrigger className="w-full min-w-0 [&>span]:min-w-0">
                <Flex
                  align="center"
                  gap="8px"
                  flex="1"
                  minW={0}
                  overflow="hidden"
                  data-esign-token-doc={document.key}
                >
                  <Box flexShrink={0} display="flex">
                    {isInvalid ? (
                      <AlertTriangle size={16} color={colors['error-500']} />
                    ) : (
                      <CheckCircle size={16} color={colors['success-200']} />
                    )}
                  </Box>
                  {/* Uploaded names are long and the rail is a fixed 340px, so
                      clip rather than push the chevron out of the panel. Text's
                      own tooltip only appears when it actually overflows. */}
                  <Box minW={0} overflow="hidden">
                    <Text
                      variant="p"
                      color={isInvalid ? 'error' : undefined}
                      truncate
                      tooltip
                    >
                      {document.filename}
                    </Text>
                  </Box>
                </Flex>
              </AccordionTrigger>
              <AccordionContent>
                <Box p="8px 16px 16px">
                  {invalidTokens.length > 0 && (
                    <Box mb="12px">
                      <Box mb="4px">
                        <Text variant="overhead">
                          {ESIGN_ACTIVITY_COPY.INVALID_TOKENS_HEADING}
                        </Text>
                      </Box>
                      {/* Without this the rail states the problem and not the
                          remedy, and a stray `{{...}}` in prose reads as a
                          product bug rather than as text Docusign will leave
                          alone. */}
                      <Box mb="8px">
                        <Text variant="small" color="muted">
                          {ESIGN_ACTIVITY_COPY.INVALID_TOKENS_HINT}
                        </Text>
                      </Box>
                      <Flex flexWrap="wrap" gap="4px" minW={0}>
                        {invalidTokens.map((token) => (
                          <InvalidTokenChip key={token} token={token} />
                        ))}
                      </Flex>
                    </Box>
                  )}
                  {validTokens.length > 0 && (
                    <Box>
                      <Box mb="8px">
                        <Text variant="overhead">
                          {ESIGN_ACTIVITY_COPY.VALID_TOKENS_HEADING}
                        </Text>
                      </Box>
                      <Flex flexDir="column" gap="12px">
                        {validTokens.map((token) => (
                          <TokenRow key={token} token={token} labels={labels} />
                        ))}
                      </Flex>
                    </Box>
                  )}
                  {invalidTokens.length === 0 && validTokens.length === 0 && (
                    <Text color="subtle">
                      {ESIGN_ACTIVITY_COPY.NO_TOKENS_FOUND}
                    </Text>
                  )}
                </Box>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </AccordionRoot>
    </Box>
  );
};

export interface EsignTokensPanelProps {
  documents: EsignWizardDocument[];
}

export interface EsignTokensPanelHandle {
  /** Open one document's tokens and scroll to them, switching tab if needed. */
  reveal: (key: string) => void;
}

/**
 * The wizard's right rail: the six anchors a document may carry, and what each
 * uploaded document actually contains.
 *
 * Defaults to "Tokens included" once anything is uploaded — that is the tab a
 * recruiter needs after a failed validation, and landing on the reference list
 * instead would hide the reason Next is blocked.
 */
export const EsignTokensPanel = forwardRef<
  EsignTokensPanelHandle,
  EsignTokensPanelProps
>(({ documents }, ref) => {
  const hasDocuments = documents.length > 0;
  // Controlled rather than `defaultValue` so `reveal` can pull the rail over to
  // this tab; the initial value is the one `defaultValue` gave.
  const [tab, setTab] = useState(hasDocuments ? 'included' : 'available');
  // Every file starts expanded: the tokens are the reason this rail exists, and
  // a recruiter checking what a document will carry should not have to open
  // each one. Collapsing is theirs to do, so this is controlled rather than a
  // `defaultValue` — that would only seed the first render, leaving a file
  // uploaded later closed while the rest are open.
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const seen = useRef<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fresh = documents
      .map((document) => document.key)
      .filter((key) => !seen.current.has(key));
    if (fresh.length === 0) return;

    fresh.forEach((key) => seen.current.add(key));
    // Only the newly-arrived keys are added, so a file the recruiter collapsed
    // stays collapsed when the next one is uploaded.
    setOpenKeys((previous) => [...previous, ...fresh]);
  }, [documents]);

  useImperativeHandle(ref, () => ({
    reveal: (key: string) => {
      setTab('included');
      setOpenKeys((previous) =>
        previous.includes(key) ? previous : [...previous, key]
      );
      // A frame later, so the row is measured where the expansion leaves it.
      requestAnimationFrame(() => {
        Array.from(
          listRef.current?.querySelectorAll('[data-esign-token-doc]') ?? []
        )
          .find((el) => el.getAttribute('data-esign-token-doc') === key)
          ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      });
    },
  }));

  return (
    <Box
      w="340px"
      flexShrink={0}
      borderLeft="1px solid"
      borderColor="border-color"
      overflowY="auto"
      data-test-id="esign-tokens-panel"
    >
      <Tabs.Root
        // Figma 7:4983: the rail's tab list is inset 16px with a 16px gap, sits
        // 10px down from the panel top, and leaves 20px between its rule and
        // the content below. kula's panel carries a 16px top margin that
        // stacked with the content's own padding and doubled that gap.
        listClassName="pl-4 gap-4"
        tabListWrapperClassName="pt-[10px]"
        panelClassName="!mt-0"
        value={tab}
        onValueChange={setTab}
        items={[
          {
            value: 'available',
            label: ESIGN_ACTIVITY_COPY.TAB_AVAILABLE,
            panel: <AvailableTokens />,
          },
          {
            value: 'included',
            label: ESIGN_ACTIVITY_COPY.TAB_INCLUDED,
            panel: (
              <IncludedTokens
                documents={documents}
                openKeys={openKeys}
                onOpenChange={setOpenKeys}
                listRef={listRef}
              />
            ),
          },
        ]}
      />
    </Box>
  );
});

EsignTokensPanel.displayName = 'EsignTokensPanel';
